/**
 * Token-refresh coordination. A single "refresher" is registered for the active
 * session (dev mode supplies one that re-auths against Keycloak; host mode
 * supplies one that nudges the host and waits for a pushed token). Both the
 * proactive expiry scheduler and the axios 401 interceptor call
 * {@link refreshAccessToken}, which de-dupes concurrent callers so a burst of
 * 401s triggers exactly one refresh.
 *
 * Contract: a refresher MUST update the in-memory token store with the new token
 * before resolving, and returns the new access token (or null if it couldn't).
 */
export type Refresher = () => Promise<string | null>;

let refresher: Refresher | null = null;
let inflight: Promise<string | null> | null = null;

export function setRefresher(next: Refresher | null): void {
  refresher = next;
  inflight = null;
}

export function clearRefresher(): void {
  refresher = null;
  inflight = null;
}

export function hasRefresher(): boolean {
  return refresher !== null;
}

/** Refresh the access token, sharing one in-flight attempt across callers. */
export function refreshAccessToken(): Promise<string | null> {
  if (!refresher) return Promise.resolve(null);
  if (!inflight) {
    inflight = refresher()
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}
